import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, HelpCircle, GraduationCap } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { EmptyState } from '@/components/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { flattenNodes } from '@/lib/node-tree';
import { useAuthStore } from '@/stores/auth';
import type { NodesResponse } from '@/stores/scope';
import type { RosterCell, RosterMember, RosterResponse } from './types';
import { classifyDevelopmentPlanBadge } from './development-plan-badge';
import { MemberDetailDrawer } from './MemberDetailDrawer';

/**
 * チームロースタータブ (`GET /talent/dashboard/roster`、設計書§1-1/§3-4)。
 * 行=メンバー、列=人材像。セルクリックで詳細ドロワーを開く (別セル再クリックでも閉じずに更新する)。
 *
 * ⚠️ APIレスポンスは snake_case (実装側連絡により設計書のcamelCase例から訂正、./types.ts参照)。
 */

type DrawerTarget = { employeeId: string; employeeName: string; contextRoleProfileId: string };

export function RosterTab({ initialNodeId }: { initialNodeId: string }) {
  const user = useAuthStore((s) => s.user);
  const [selectedNodeId, setSelectedNodeId] = useState(initialNodeId);
  const [deselectedRoleProfileIds, setDeselectedRoleProfileIds] = useState<Set<string>>(new Set());
  const [mobileEmployeeId, setMobileEmployeeId] = useState('');
  const [drawerTarget, setDrawerTarget] = useState<DrawerTarget | null>(null);

  const { data: nodesData, isLoading: nodesLoading, isError: nodesError } = useQuery<NodesResponse>({
    queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes'),
  });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  // own_store/node_scope のユーザーは user.stores (JWT scope_stores) に含まれるノード以外を渡すと
  // isResourceInPermissionScope に弾かれる (CompetencyMatrixTab.tsx と同じ理由、D-636)。
  const selectableNodes = useMemo(() => (
    user?.stores && user.stores.length > 0
      ? flatNodes.filter((n) => user.stores!.includes(n.node.node_id))
      : flatNodes
  ), [flatNodes, user?.stores]);
  const nodeId = selectedNodeId || selectableNodes[0]?.node.node_id || '';

  type RoleProfileMaster = { role_profile_id: string; label: string };
  const { data: roleProfilesMaster = [] } = useQuery<RoleProfileMaster[]>({
    queryKey: ['role-profiles'], queryFn: () => apiFetch('/masters/role-profiles'),
  });
  const includedRoleProfileIds = roleProfilesMaster
    .map((rp) => rp.role_profile_id)
    .filter((id) => !deselectedRoleProfileIds.has(id));
  const toggleRoleProfile = (id: string) => setDeselectedRoleProfileIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const query = new URLSearchParams({ node_id: nodeId });
  // 省略時は全件 (設計書§1-1)。一部だけ外している時のみ絞り込みパラメータを付ける。
  if (deselectedRoleProfileIds.size > 0 && includedRoleProfileIds.length > 0) {
    query.set('role_profile_ids', includedRoleProfileIds.join(','));
  }
  const { data, isLoading, isError, error } = useQuery<RosterResponse>({
    queryKey: ['talent-dashboard-roster', nodeId, includedRoleProfileIds.join(',')],
    queryFn: () => apiFetch<RosterResponse>(`/talent/dashboard/roster?${query.toString()}`),
    enabled: !!nodeId,
    retry: false,
  });

  const members = data?.members ?? [];
  const roleProfiles = data?.role_profiles ?? [];
  const cellByRole = (member: RosterMember) => new Map(member.cells.map((c) => [c.role_profile_id, c]));

  const openDrawer = (employeeId: string, employeeName: string, contextRoleProfileId: string) =>
    setDrawerTarget({ employeeId, employeeName, contextRoleProfileId });

  const renderDevelopmentPlanBadge = (dp: RosterMember['development_plan']) => {
    if (!dp?.has_active) return null;
    const state = classifyDevelopmentPlanBadge(dp.nearest_due_date);
    // ⚠️ [実装後レビューで是正、design-expert Minor]: danger.* はエラー・破壊的操作専用の配色
    // (CLAUDE.md ui-guidelines)。期限警告はいずれも alert.* 系で2段階に留める (反復原則の一貫性)。
    const cls = state === 'overdue'
      ? 'bg-alert-bg text-alert-text border-alert-border font-semibold'
      : state === 'near'
        ? 'bg-alert-bg text-alert-text border-alert-border'
        : 'bg-surface-sunken text-text-sub border-surface-border';
    // ⚠️ [実装後レビューで是正]: 通常状態は設計書§3-4どおり日付未設定時は「-」、設定済みなら日付を表示。
    const label = state === 'overdue' ? '期限超過' : state === 'near' ? '期限間近' : (dp.nearest_due_date ?? '-');
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${cls}`}>
        <GraduationCap size={11} aria-hidden />{label}
      </span>
    );
  };

  const renderCell = (cell: RosterCell | undefined) => {
    if (!cell) return <span className="text-xs text-text-sub">—</span>;
    if (cell.undeterminable) {
      return (
        <div className="flex flex-col items-center gap-0.5 text-text-sub" title="尺度未設定・無効化等のため判定できません">
          <HelpCircle size={14} aria-hidden />
          <span className="text-[11px] whitespace-nowrap">判定不能</span>
        </div>
      );
    }
    if (cell.total_count === 0) return <span className="text-xs text-text-sub">—</span>;
    const satisfied = cell.satisfied_count >= cell.total_count;
    return (
      <div className={`flex flex-col items-center gap-0.5 ${satisfied ? 'text-success-text' : 'text-alert-text'}`}>
        {satisfied ? <CheckCircle2 size={14} aria-hidden /> : <AlertTriangle size={14} aria-hidden />}
        <span className="text-xs font-semibold whitespace-nowrap">{cell.satisfied_count}/{cell.total_count}</span>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {nodesLoading ? (
        <div role="status" className="text-text-sub">読み込み中…</div>
      ) : nodesError || selectableNodes.length === 0 ? (
        <EmptyState message="店舗が見つかりません" description="組織階層が設定されていないか、閲覧できる店舗がありません。" />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <div className="max-w-xs">
              <label className="block text-xs text-text-sub mb-1" htmlFor="roster-node">対象店舗</label>
              <Select value={nodeId || '__unselected__'} onValueChange={(v) => setSelectedNodeId(v === '__unselected__' ? '' : v)}>
                <SelectTrigger id="roster-node" aria-label="対象店舗"><SelectValue placeholder="選択してください" /></SelectTrigger>
                <SelectContent>
                  {selectableNodes.map(({ node }) => <SelectItem key={node.node_id} value={node.node_id}>{node.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {roleProfilesMaster.length > 0 && (
              <div className="min-w-0">
                <div className="text-xs text-text-sub mb-1">表示する人材像</div>
                <div className="flex flex-wrap gap-2">
                  {roleProfilesMaster.map((rp) => (
                    <label key={rp.role_profile_id}
                      // ⚠️ [実装後レビューで是正、design-expert Major]: min-h-[32px]は44px未満の
                      // タップ領域だった。EmployeeSkillsPanel.tsx の既存パターンに合わせ44pxを確保。
                      className="inline-flex items-center gap-1.5 text-xs border border-surface-border rounded-full px-2.5 min-h-[44px] cursor-pointer has-[:checked]:border-misespark-navy has-[:checked]:bg-misespark-navy/5">
                      <input type="checkbox" className="w-4 h-4 accent-misespark-navy" checked={!deselectedRoleProfileIds.has(rp.role_profile_id)}
                        onChange={() => toggleRoleProfile(rp.role_profile_id)} aria-label={`${rp.label} を表示に含める`} />
                      {rp.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {!nodeId ? (
            <p className="text-sm text-text-sub">上の「対象店舗」からまず選択してください。</p>
          ) : isLoading ? (
            <div role="status" className="text-text-sub">読み込み中…</div>
          ) : isError ? (
            <p role="alert" className="text-sm text-alert-text">
              {error instanceof ApiClientError && error.code === 'OUT_OF_SCOPE'
                ? 'この店舗のロースターを閲覧する権限がありません。' : 'ロースターの取得に失敗しました。'}
            </p>
          ) : members.length === 0 ? (
            <EmptyState message="この店舗の在籍者が見つかりませんでした" />
          ) : (
            <>
              {/* ≥768px: メンバー×人材像のグリッド (D-647と同じパターン) */}
              <div className="hidden md:block border border-surface-border rounded-lg overflow-hidden bg-white">
                <div className="overflow-x-auto">
                  <table className="border-collapse text-sm">
                    <thead>
                      <tr>
                        <th scope="col" className="sticky left-0 z-10 bg-surface-sunken border-b border-r border-surface-border px-3 py-2 text-left text-xs text-text-sub" style={{ minWidth: 180 }}>
                          対象者
                        </th>
                        {roleProfiles.map((rp) => (
                          <th key={rp.role_profile_id} scope="col" className="border-b border-surface-border px-2 py-2 text-center text-xs text-text-sub whitespace-nowrap" style={{ minWidth: 96 }}>
                            {rp.label}
                          </th>
                        ))}
                        {roleProfiles.length === 0 && (
                          <th scope="col" className="border-b border-surface-border px-3 py-2 text-center text-xs text-text-sub">人材像がまだ登録されていません</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => {
                        const byRole = cellByRole(member);
                        return (
                          <tr key={member.employee_id} className="odd:bg-white even:bg-surface-stripe">
                            <th scope="row" className="sticky left-0 z-10 bg-inherit border-r border-surface-border px-3 py-2 text-left align-top">
                              <button type="button"
                                onClick={() => openDrawer(member.employee_id, member.display_name, roleProfiles[0]?.role_profile_id ?? '')}
                                className="text-left min-h-[44px] sm:min-h-0">
                                <div className="text-xs font-semibold text-text-body break-words underline underline-offset-2">{member.display_name}</div>
                                {member.position_level_label && <div className="text-[11px] text-text-sub break-words">{member.position_level_label}</div>}
                              </button>
                              <div className="mt-1">{renderDevelopmentPlanBadge(member.development_plan)}</div>
                            </th>
                            {roleProfiles.map((rp) => (
                              <td key={rp.role_profile_id} className="px-2 py-2 text-center align-top">
                                <button type="button" onClick={() => openDrawer(member.employee_id, member.display_name, rp.role_profile_id)}
                                  className="w-full min-h-[44px] sm:min-h-0 flex items-center justify-center focus-ring rounded">
                                  {renderCell(byRole.get(rp.role_profile_id))}
                                </button>
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* <768px: 対象者を選択してドリルダウン (D-647と同じパターン) */}
              <div className="md:hidden space-y-2">
                <div className="max-w-xs">
                  <label className="block text-xs text-text-sub mb-1" htmlFor="roster-employee-mobile">対象者</label>
                  <Select value={mobileEmployeeId || '__none__'} onValueChange={(v) => setMobileEmployeeId(v === '__none__' ? '' : v)}>
                    <SelectTrigger id="roster-employee-mobile" aria-label="対象者"><SelectValue placeholder="選択してください" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">選択してください</SelectItem>
                      {members.map((m) => <SelectItem key={m.employee_id} value={m.employee_id}>{m.display_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {!mobileEmployeeId ? (
                  <p className="text-sm text-text-sub">上の「対象者」からまず選択してください。</p>
                ) : (
                  (() => {
                    const member = members.find((m) => m.employee_id === mobileEmployeeId);
                    if (!member) return null;
                    const byRole = cellByRole(member);
                    return (
                      <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
                        <div>
                          <button type="button" onClick={() => openDrawer(member.employee_id, member.display_name, roleProfiles[0]?.role_profile_id ?? '')}
                            className="text-left min-h-[44px]">
                            <div className="text-sm font-semibold text-text-body break-words underline underline-offset-2">{member.display_name}</div>
                            {member.position_level_label && <div className="text-xs text-text-sub break-words">{member.position_level_label}</div>}
                          </button>
                          <div className="mt-1">{renderDevelopmentPlanBadge(member.development_plan)}</div>
                        </div>
                        {roleProfiles.length === 0 ? (
                          <p className="text-sm text-text-sub">人材像がまだ登録されていません。</p>
                        ) : (
                          <ul className="space-y-2">
                            {roleProfiles.map((rp) => (
                              <li key={rp.role_profile_id} className="flex items-center justify-between gap-2 border-t border-surface-line pt-2 first:border-t-0 first:pt-0">
                                <span className="text-sm text-text-body break-words">{rp.label}</span>
                                <button type="button" onClick={() => openDrawer(member.employee_id, member.display_name, rp.role_profile_id)} className="min-h-[44px] px-1">
                                  {renderCell(byRole.get(rp.role_profile_id))}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            </>
          )}
        </>
      )}

      <MemberDetailDrawer
        open={!!drawerTarget}
        employeeId={drawerTarget?.employeeId ?? ''}
        employeeName={drawerTarget?.employeeName}
        contextRoleProfileId={drawerTarget?.contextRoleProfileId ?? ''}
        onClose={() => setDrawerTarget(null)}
      />
    </div>
  );
}
